import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import useWindowDimensions from 'utils/useWindowDimensions';
import type { ComponentsLogoBannerLogo } from 'queries/graphql';

type RotatingBannerProps = {
  images: Array<ComponentsLogoBannerLogo>;
  styles: any;
};

export default function RotatingBanner({ images, styles }: RotatingBannerProps) {
  const { width } = useWindowDimensions();
  const [bannerCount, setBannerCount] = useState<number | null>(null);

  useEffect(() => {
    if (images?.length > 0) {
      // 180 is the image width plus its horizontal margins specified in css module for each image
      setBannerCount(Math.ceil(width / (images.length * 180)));
    }
  }, [width, images.length]);

  const WrappedImages = useMemo(() => {
    return images.map(({ image }, index) => (
      <div className={styles.image_wrapper} key={index}>
        {image && (
          <Image
            src={image.src}
            alt={image.alt}
            priority
            width={image.width}
            height={image.height}
          />
        )}
      </div>
    ));
  }, [images, styles.image_wrapper]);

  const Banner = useMemo(() => {
    if (bannerCount) {
      return [...Array(bannerCount)].map((_, wrappedImagesIndex) => (
        <div key={wrappedImagesIndex} className={styles.images_wrapper}>
          {WrappedImages}
        </div>
      ));
    }

    return null;
  }, [WrappedImages, bannerCount, styles.images_wrapper]);

  if (bannerCount === null) {
    // Optionally, you can return a placeholder or nothing until bannerCount is calculated
    return null;
  }

  return (
    <div className={styles.banner_sets_wrapper}>
      {[...Array(2)].map((_, bannerIndex) => (
        <div className={styles.container} key={bannerIndex}>
          {Banner}
        </div>
      ))}
    </div>
  );
}
